import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import CameraScannerButton from "@/components/CameraScannerButton";
import SignaturePad from "@/components/SignaturePad";
import AuthImage from "@/components/AuthImage";
import { buildVolumeLabelsHTML, openPrint } from "@/lib/print";
import { fmtDate, CARGA_STATUS, OCORR_TRANSPORTE } from "@/lib/meta";
import { Loader2, Truck, Package, Printer, ScanLine, CheckCircle2, XCircle, MapPin, PackageCheck, ClipboardSignature, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const VOL_CLS = {
  PENDENTE: "bg-slate-800/70 text-slate-300 border-slate-600",
  EMBARCADO: "bg-sky-950/70 text-sky-300 border-sky-500",
  ENTREGUE: "bg-emerald-900/70 text-emerald-300 border-emerald-400",
  DIVERGENTE: "bg-rose-950/70 text-rose-300 border-rose-500",
};

export default function CargaDetalhe() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const isTransporte = user.role === "TRANSPORTE";
  const isOverride = ["GESTOR", "MASTER", "SUPER_ADMIN"].includes(user.role);
  const isCD = ["SUPER_ADMIN", "MASTER", "GESTOR", "SUPERVISOR", "ESTOQUISTA"].includes(user.role);
  const isAssignRole = ["GESTOR", "SUPERVISOR", "MASTER", "SUPER_ADMIN"].includes(user.role);

  const [motoristas, setMotoristas] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const [assign, setAssign] = useState({ motorista_id: "", veiculo_id: "" });
  const [printOpen, setPrintOpen] = useState(false);
  const [excModal, setExcModal] = useState(false);
  const [excMotivo, setExcMotivo] = useState("");
  const [compModal, setCompModal] = useState(false);
  const [comp, setComp] = useState({ recebedor_nome: "", assinatura_path: null, foto_path: null });
  const [ocModal, setOcModal] = useState(false);
  const [oc, setOc] = useState({ tipo: "VOLUME_AVARIADO", volume_id: "", observacao: "", foto_path: null });

  const load = useCallback(() => api.get(`/cargas/${id}`).then((r) => setC(r.data)).catch(() => nav("/cargas")), [id, nav]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isAssignRole) {
      api.get("/motoristas").then((r) => setMotoristas(r.data)).catch(() => {});
      api.get("/veiculos").then((r) => setVeiculos(r.data.filter((v) => v.ativo))).catch(() => {});
    }
  }, [isAssignRole]);

  if (!c) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const st = c.status;
  const vols = c.volumes || [];
  const embarcados = vols.filter((v) => v.status === "EMBARCADO" || v.status === "ENTREGUE").length;
  const entregues = vols.filter((v) => v.status === "ENTREGUE").length;
  const meta = CARGA_STATUS[st] || {};
  const canOperar = isTransporte ? c.motorista_id === user.id : isOverride;

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };

  const salvarAtribuir = () => act(async () => {
    if (!assign.motorista_id) throw { response: { data: { detail: "Selecione o motorista" } } };
    await api.put(`/cargas/${id}/atribuir`, { motorista_id: assign.motorista_id, veiculo_id: assign.veiculo_id || null });
  }, "Motorista atribuído");

  const biparEmbarque = (codigo) => act(async () => {
    const r = await api.post(`/cargas/${id}/embarque/bipar`, { codigo });
    toast.success(`Volume ${r.data.seq}/${r.data.total} embarcado (${r.data.volumes_embarcados}/${r.data.volumes_total})`);
  });
  const biparEntrega = (codigo) => act(async () => {
    const r = await api.post(`/cargas/${id}/entrega/bipar`, { codigo });
    toast.success(`Volume ${r.data.seq}/${r.data.total} conferido (${r.data.volumes_entregues}/${r.data.volumes_total})`);
  });

  const concluirEmbarque = () => {
    const pend = vols.filter((v) => v.status === "PENDENTE").length;
    if (pend > 0) { setExcMotivo(""); setExcModal(true); return; }
    act(() => api.post(`/cargas/${id}/embarque/concluir`, {}), "Embarque concluído — carga em transporte");
  };
  const confirmExcecao = () => act(async () => {
    await api.post(`/cargas/${id}/embarque/concluir`, { excecao_motivo: excMotivo });
    setExcModal(false);
  }, "Embarque concluído com exceção");

  const uploadFoto = async (file, setter) => {
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setter(r.data.path); toast.success("Foto anexada"); }
    catch { toast.error("Falha no upload"); }
  };

  const salvarComprovante = () => act(async () => {
    if (!comp.recebedor_nome.trim()) throw { response: { data: { detail: "Informe quem recebeu" } } };
    await api.post(`/cargas/${id}/entrega/concluir`, comp);
    setCompModal(false);
  }, "Entrega concluída");

  const salvarOcorrencia = () => act(async () => {
    await api.post(`/cargas/${id}/ocorrencia`, { ...oc, volume_id: oc.volume_id || null });
    setOcModal(false); setOc({ tipo: "VOLUME_AVARIADO", volume_id: "", observacao: "", foto_path: null });
  }, "Ocorrência registrada");

  const imprimirEtiquetas = (formato) => {
    const items = vols.map((v) => {
      const cv = document.getElementById(`qrvol-${v.id}`);
      return { qr: cv ? cv.toDataURL("image/png") : "", pedido_numero: v.pedido_numero, carga_numero: c.numero,
               seq: v.seq, total: v.total, origem: "CD", destino: (c.pedidos || []).find((p) => p.id === v.pedido_id)?.numero || "Unidade", payload: v.qr_payload };
    });
    openPrint(buildVolumeLabelsHTML({ volumes: items, formato }));
    setPrintOpen(false);
  };

  return (
    <div className="space-y-5" data-testid="carga-detalhe">
      <div style={{ position: "absolute", left: -99999, top: 0 }} aria-hidden>
        {vols.map((v) => <QRCodeCanvas key={v.id} id={`qrvol-${v.id}`} value={v.qr_payload} size={200} level="M" includeMargin />)}
      </div>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <button onClick={() => nav("/cargas")} className="text-xs text-muted-foreground hover:text-primary">← Cargas</button>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase flex items-center gap-2"><Truck className="w-7 h-7 text-primary" /> {c.numero}</h1>
          <div className="text-xs text-muted-foreground mt-1">{fmtDate(c.created_at)} · {c.pedidos?.length || 0} pedido(s)</div>
        </div>
        <span className={`inline-flex rounded-sm border px-2.5 py-1 text-xs font-bold uppercase font-mono ${meta.cls}`} data-testid="carga-status">{meta.label}</span>
      </div>

      <Card className="bg-card/70 p-4 grid sm:grid-cols-3 gap-3">
        <div><div className="text-[10px] uppercase text-muted-foreground">Motorista</div><div className="font-semibold">{c.motorista_nome || "— não atribuído —"}</div></div>
        <div><div className="text-[10px] uppercase text-muted-foreground">Veículo</div><div className="font-semibold font-mono">{c.veiculo_placa || "—"}</div></div>
        <div><div className="text-[10px] uppercase text-muted-foreground">Volumes</div><div className="font-mono font-bold text-primary">{embarcados}/{c.volumes_total} embarcados · {entregues} entregues</div></div>
      </Card>

      {/* Atribuição (CD) */}
      {isAssignRole && ["AGUARDANDO_COLETA", "EM_EMBARQUE"].includes(st) && (
        <Card className="bg-card/70 p-4 space-y-3" data-testid="atribuir-card">
          <div className="font-display text-sm font-bold uppercase">Atribuir motorista e veículo</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Motorista</Label>
              <Select value={assign.motorista_id} onValueChange={(v) => setAssign((a) => ({ ...a, motorista_id: v }))}>
                <SelectTrigger data-testid="sel-motorista"><SelectValue placeholder="Selecione…" /></SelectTrigger>
                <SelectContent>{motoristas.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Veículo (opcional)</Label>
              <Select value={assign.veiculo_id} onValueChange={(v) => setAssign((a) => ({ ...a, veiculo_id: v }))}>
                <SelectTrigger data-testid="sel-veiculo"><SelectValue placeholder="Selecione…" /></SelectTrigger>
                <SelectContent>{veiculos.map((v) => <SelectItem key={v.id} value={v.id}>{v.placa} · {v.tipo}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <Button onClick={salvarAtribuir} disabled={busy} data-testid="btn-salvar-atribuir"><Truck className="w-4 h-4" /> Salvar atribuição</Button>
        </Card>
      )}

      {/* Ações CD: etiquetas + cancelar */}
      {isCD && (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setPrintOpen(true)} data-testid="btn-imprimir-volumes"><Printer className="w-4 h-4" /> Imprimir etiquetas de volume</Button>
          {["GESTOR", "MASTER", "SUPER_ADMIN"].includes(user.role) && ["AGUARDANDO_COLETA", "EM_EMBARQUE"].includes(st) && (
            <Button variant="outline" className="text-red-400 border-red-500/40" onClick={() => act(() => api.post(`/cargas/${id}/cancelar`), "Carga cancelada").then(() => nav("/cargas"))} disabled={busy} data-testid="btn-cancelar-carga"><XCircle className="w-4 h-4" /> Cancelar carga</Button>
          )}
        </div>
      )}

      {/* Ações do motorista / campo */}
      {canOperar && (
        <Card className="bg-card/70 p-4 space-y-3" data-testid="acoes-transporte">
          <div className="font-display text-sm font-bold uppercase">Operação de transporte</div>
          {["AGUARDANDO_COLETA", "EM_EMBARQUE"].includes(st) && (
            <div className="space-y-2">
              <div className="text-xs text-muted-foreground">Leia o QR de cada volume para embarcar.</div>
              <div className="flex gap-2">
                <ScanBox onScan={biparEmbarque} testid="scan-embarque" />
              </div>
              <Button className="w-full" onClick={concluirEmbarque} disabled={busy || !c.motorista_id} data-testid="btn-concluir-embarque"><PackageCheck className="w-4 h-4" /> Concluir embarque ({embarcados}/{c.volumes_total})</Button>
              {!c.motorista_id && <div className="text-[11px] text-amber-300">Aguardando atribuição de motorista.</div>}
            </div>
          )}
          {st === "EM_TRANSPORTE" && <Button className="w-full" onClick={() => act(() => api.post(`/cargas/${id}/chegada`), "Chegada registrada")} disabled={busy} data-testid="btn-chegada"><MapPin className="w-4 h-4" /> Registrar chegada ao destino</Button>}
          {st === "CHEGADA" && <Button className="w-full" onClick={() => act(() => api.post(`/cargas/${id}/descarga`), "Descarga iniciada")} disabled={busy} data-testid="btn-descarga"><Package className="w-4 h-4" /> Iniciar descarga</Button>}
          {["CHEGADA", "EM_DESCARGA"].includes(st) && (
            <div className="space-y-2">
              <div className="text-xs text-muted-foreground">Confira os volumes na entrega e finalize com o comprovante.</div>
              <ScanBox onScan={biparEntrega} testid="scan-entrega" />
              <Button className="w-full" onClick={() => { setComp({ recebedor_nome: "", assinatura_path: null, foto_path: null }); setCompModal(true); }} disabled={busy} data-testid="btn-abrir-comprovante"><ClipboardSignature className="w-4 h-4" /> Concluir entrega (comprovante)</Button>
            </div>
          )}
          {["EM_TRANSPORTE", "CHEGADA", "EM_DESCARGA", "EM_EMBARQUE"].includes(st) && (
            <Button variant="outline" className="w-full text-rose-300 border-rose-500/40" onClick={() => setOcModal(true)} disabled={busy} data-testid="btn-ocorrencia-transporte"><AlertTriangle className="w-4 h-4" /> Registrar ocorrência</Button>
          )}
        </Card>
      )}

      {/* Comprovante */}
      {st === "ENTREGUE" && c.comprovante && (
        <Card className="bg-card/70 p-4 space-y-2 border-emerald-600/40" data-testid="comprovante-card">
          <div className="font-display text-sm font-bold uppercase text-emerald-300 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Comprovante de Entrega</div>
          <div className="text-sm">Recebido por: <b>{c.comprovante.recebedor_nome}</b></div>
          <div className="text-xs text-muted-foreground">{fmtDate(c.comprovante.data_hora)} · registrado por {c.comprovante.registrado_por_nome}</div>
          {c.entrega_parcial && <div className="text-[11px] text-amber-300">Entrega com ressalva (volumes divergentes).</div>}
          <div className="flex gap-3 flex-wrap">
            {c.comprovante.assinatura_path && <div><div className="text-[10px] uppercase text-muted-foreground mb-1">Assinatura</div><AuthImage path={c.comprovante.assinatura_path} className="w-48 h-24 object-contain bg-white rounded" /></div>}
            {c.comprovante.foto_path && <div><div className="text-[10px] uppercase text-muted-foreground mb-1">Foto</div><AuthImage path={c.comprovante.foto_path} className="w-32 h-24 object-cover rounded" /></div>}
          </div>
        </Card>
      )}

      {/* Volumes */}
      <Card className="bg-card/70 p-4">
        <div className="font-display text-lg font-bold uppercase mb-3">Volumes ({vols.length})</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="volumes-grid">
          {vols.map((v) => (
            <div key={v.id} className={`rounded-md border p-2 text-center ${VOL_CLS[v.status] || VOL_CLS.PENDENTE}`} data-testid={`volume-${v.pedido_numero}-${v.seq}`}>
              <div className="font-mono font-bold text-sm">{v.seq}/{v.total}</div>
              <div className="text-[10px] opacity-80">{v.pedido_numero}</div>
              <div className="text-[9px] uppercase font-bold mt-0.5">{v.status}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Ocorrências */}
      {(c.ocorrencias || []).length > 0 && (
        <Card className="bg-card/70 p-4" data-testid="carga-ocorrencias">
          <div className="font-display text-lg font-bold uppercase mb-3">Ocorrências de transporte</div>
          <div className="space-y-2">
            {c.ocorrencias.map((o) => (
              <div key={o.id} className="border border-rose-500/30 rounded-md p-2.5 text-sm" data-testid={`oc-${o.id}`}>
                <div className="flex items-center justify-between"><span className="font-bold text-rose-300">{OCORR_TRANSPORTE.find((t) => t.id === o.tipo)?.label || o.tipo}</span><span className="text-[11px] text-muted-foreground">{fmtDate(o.created_at)}</span></div>
                {o.volume_seq && <div className="text-xs text-muted-foreground">Volume {o.volume_seq} · {o.pedido_numero}</div>}
                {o.observacao && <div className="text-xs mt-0.5">{o.observacao}</div>}
                {o.foto_path && <AuthImage path={o.foto_path} className="w-24 h-20 object-cover rounded mt-1" />}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Modal imprimir etiquetas */}
      <Dialog open={printOpen} onOpenChange={setPrintOpen}>
        <DialogContent className="max-w-sm" data-testid="print-volumes-modal">
          <DialogHeader><DialogTitle className="font-display uppercase">Etiquetas de Volume</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Uma etiqueta por volume, com QR único. Escolha o formato:</p>
          <div className="flex flex-col gap-2">
            <Button onClick={() => imprimirEtiquetas("ETIQUETA57")} data-testid="btn-print-57"><Printer className="w-4 h-4" /> Etiqueta adesiva 57×50 mm</Button>
            <Button variant="outline" onClick={() => imprimirEtiquetas("MINI58")} data-testid="btn-print-58"><Printer className="w-4 h-4" /> Bobina 58 mm (contínua)</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal exceção embarque */}
      <Dialog open={excModal} onOpenChange={setExcModal}>
        <DialogContent className="max-w-md" data-testid="excecao-modal">
          <DialogHeader><DialogTitle className="font-display uppercase">Concluir com volume faltante</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            <div className="text-muted-foreground">Há volumes não embarcados. Esta exceção exige autorização de Gestor/Master e gera ocorrência auditada.</div>
            <Textarea value={excMotivo} onChange={(e) => setExcMotivo(e.target.value)} placeholder="Motivo da exceção" data-testid="excecao-motivo" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExcModal(false)}>Voltar</Button>
            <Button className="bg-amber-500 hover:bg-amber-400 text-black" onClick={confirmExcecao} disabled={busy || !excMotivo.trim()} data-testid="btn-confirmar-excecao">Autorizar e concluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal comprovante */}
      <Dialog open={compModal} onOpenChange={setCompModal}>
        <DialogContent className="max-w-md" data-testid="comprovante-modal">
          <DialogHeader><DialogTitle className="font-display uppercase">Comprovante de Entrega</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome de quem recebeu *</Label><Input value={comp.recebedor_nome} onChange={(e) => setComp((s) => ({ ...s, recebedor_nome: e.target.value }))} data-testid="comp-recebedor" /></div>
            <div><Label>Assinatura</Label><SignaturePad onChange={async (dataUrl) => {
              if (!dataUrl) { setComp((s) => ({ ...s, assinatura_path: null })); return; }
              const blob = await (await fetch(dataUrl)).blob();
              const fd = new FormData(); fd.append("file", new File([blob], "assinatura.png", { type: "image/png" }));
              try { const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setComp((s) => ({ ...s, assinatura_path: r.data.path })); } catch { toast.error("Falha ao salvar assinatura"); }
            }} /></div>
            <div><Label>Foto da entrega (opcional)</Label><Input type="file" accept="image/*" onChange={(e) => e.target.files[0] && uploadFoto(e.target.files[0], (p) => setComp((s) => ({ ...s, foto_path: p })))} data-testid="comp-foto" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompModal(false)}>Cancelar</Button>
            <Button onClick={salvarComprovante} disabled={busy} data-testid="btn-salvar-comprovante"><CheckCircle2 className="w-4 h-4" /> Concluir entrega</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal ocorrência */}
      <Dialog open={ocModal} onOpenChange={setOcModal}>
        <DialogContent className="max-w-md" data-testid="ocorrencia-modal">
          <DialogHeader><DialogTitle className="font-display uppercase">Ocorrência de Transporte</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Tipo</Label>
              <Select value={oc.tipo} onValueChange={(v) => setOc((s) => ({ ...s, tipo: v }))}>
                <SelectTrigger data-testid="oc-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>{OCORR_TRANSPORTE.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Volume (opcional)</Label>
              <Select value={oc.volume_id} onValueChange={(v) => setOc((s) => ({ ...s, volume_id: v }))}>
                <SelectTrigger data-testid="oc-volume"><SelectValue placeholder="Nenhum específico" /></SelectTrigger>
                <SelectContent>{vols.map((v) => <SelectItem key={v.id} value={v.id}>{v.pedido_numero} · {v.seq}/{v.total}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Observação</Label><Textarea value={oc.observacao} onChange={(e) => setOc((s) => ({ ...s, observacao: e.target.value }))} data-testid="oc-obs" /></div>
            <div><Label>Foto (opcional)</Label><Input type="file" accept="image/*" onChange={(e) => e.target.files[0] && uploadFoto(e.target.files[0], (p) => setOc((s) => ({ ...s, foto_path: p })))} data-testid="oc-foto" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOcModal(false)}>Cancelar</Button>
            <Button onClick={salvarOcorrencia} disabled={busy} data-testid="btn-salvar-ocorrencia">Registrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ScanBox({ onScan, testid }) {
  const [code, setCode] = useState("");
  const submit = () => { if (code.trim()) { onScan(code.trim()); setCode(""); } };
  return (
    <div className="flex gap-2 w-full">
      <Input value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="Bipe ou digite o código do volume" data-testid={`${testid}-input`} />
      <Button variant="outline" onClick={submit} data-testid={`${testid}-submit`}><ScanLine className="w-4 h-4" /></Button>
      <CameraScannerButton continuous onDetected={(v) => onScan(v)} testid={`${testid}-cam`} />
    </div>
  );
}
